<template>
  <div class="card p-4 shadow-sm">
    <h5 class="mb-3 fw-bold">Inventario Global</h5>

    <!-- Toast de alerta -->
    <div
      v-if="mostrarToast"
      class="toast show position-fixed top-0 end-0 m-3"
      style="z-index: 9999; min-width: 280px"
    >
      <div class="toast-header" :class="toastColor">
        <i :class="iconoToast" class="me-2 text-white"></i>
        <strong class="me-auto text-white">Aviso</strong>
        <button type="button" class="btn-close btn-close-white" @click="cerrarToast"></button>
      </div>
      <div class="toast-body">{{ alerta.mensaje }}</div>
    </div>

    <!-- Filtros -->
    <div class="row mb-3 g-2">
      <div class="col-md-4">
        <input
          type="text"
          class="form-control"
          placeholder="Buscar por nombre de producto"
          v-model="filtroNombre"
        />
      </div>
      <div class="col-md-3">
        <select class="form-select" v-model="filtroSeccion">
          <option value="">Todas las secciones</option>
          <option v-for="s in secciones" :key="s" :value="s">{{ s }}</option>
        </select>
      </div>
      <div class="col-md-3">
        <select class="form-select" v-model="ordenarPor">
          <option value="">Ordenar por...</option>
          <option value="nombre-asc">Nombre (A-Z)</option>
          <option value="nombre-desc">Nombre (Z-A)</option>
          <option value="seccion-asc">Sección (A-Z)</option>
          <option value="seccion-desc">Sección (Z-A)</option>
        </select>
      </div>
      <div class="col-md-2">
        <button class="btn btn-secondary w-100" @click="limpiarFiltros">Limpiar</button>
      </div>
    </div>

    <!-- Botones de acción -->
    <div class="d-flex gap-2 mb-3">
      <button class="btn btn-danger" @click="descargarPDF" :disabled="cargando">
        <i class="bi bi-file-pdf me-1"></i> Descargar PDF
      </button>
    </div>

    <!-- Totales -->
    <div class="row mb-3 g-2" v-if="!cargando">
      <div class="col-auto">
        <div class="badge bg-primary fs-6 px-3 py-2">
          Total Bruto: Q {{ formatMonto(totalBruto) }}
        </div>
      </div>
      <div class="col-auto">
        <div class="badge bg-success fs-6 px-3 py-2">
          Total Neto (sin IVA): Q {{ formatMonto(totalNeto) }}
        </div>
      </div>
      <div class="col-auto">
        <div class="badge bg-secondary fs-6 px-3 py-2">
          {{ productosFiltrados.length }} registro(s)
        </div>
      </div>
    </div>

    <!-- Spinner -->
    <div v-if="cargando" class="text-center py-5">
      <div class="spinner-border text-primary" role="status"></div>
      <p class="mt-2 text-muted">Cargando inventarios...</p>
    </div>

    <!-- Tabla -->
    <div v-else class="table-responsive">
      <table class="table table-bordered table-hover table-sm">
        <thead class="table-dark">
          <tr>
            <th>Sección</th>
            <th>Nombre Producto</th>
            <th class="text-end">Existencia</th>
            <th class="text-end">Precio Costo</th>
            <th class="text-end">Total Bruto</th>
            <th class="text-end">Total Neto</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="productosFiltrados.length === 0">
            <td colspan="6" class="text-center text-muted py-3">No hay registros que mostrar.</td>
          </tr>
          <tr v-for="(item, i) in productosFiltrados" :key="i">
            <td>
              <span class="badge bg-light text-dark border">{{ item.seccion }}</span>
            </td>
            <td>{{ item.NombreProducto }}</td>
            <td class="text-end">{{ item.StockActual }}</td>
            <td class="text-end">Q {{ formatMonto(item.PrecioCosto) }}</td>
            <td class="text-end">Q {{ formatMonto(item.TotalBruto) }}</td>
            <td class="text-end">Q {{ formatMonto(item.TotalNeto) }}</td>
          </tr>
        </tbody>
        <tfoot>
          <tr class="table-dark fw-bold">
            <td colspan="4">TOTALES GENERALES</td>
            <td class="text-end">Q {{ formatMonto(totalBruto) }}</td>
            <td class="text-end">Q {{ formatMonto(totalNeto) }}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { API_URL } from "@/config/config.js";

export default {
  name: "InventarioGlobal",
  data() {
    return {
      todos: [],
      filtroNombre: "",
      filtroSeccion: "",
      ordenarPor: "",
      cargando: false,
      alerta: { mensaje: "", tipo: "success" },
      mostrarToast: false,
    };
  },
  computed: {
    toastColor() {
      return this.alerta.tipo === "success" ? "bg-success" : "bg-danger";
    },
    iconoToast() {
      return this.alerta.tipo === "success" ? "bi bi-check-circle" : "bi bi-exclamation-circle";
    },
    secciones() {
      return [...new Set(this.todos.map((i) => i.seccion))].sort();
    },
    productosFiltrados() {
      let lista = this.todos;

      if (this.filtroNombre.trim()) {
        const q = this.filtroNombre.trim().toLowerCase();
        lista = lista.filter((i) => (i.NombreProducto || "").toLowerCase().includes(q));
      }

      if (this.filtroSeccion) {
        lista = lista.filter((i) => i.seccion === this.filtroSeccion);
      }

      if (this.ordenarPor) {
        const [campo, dir] = this.ordenarPor.split("-");
        const factor = dir === "desc" ? -1 : 1;
        lista = lista.slice().sort((a, b) => {
          const va = campo === "seccion" ? (a.seccion || "") : (a.NombreProducto || "");
          const vb = campo === "seccion" ? (b.seccion || "") : (b.NombreProducto || "");
          return factor * va.localeCompare(vb);
        });
      }

      return lista;
    },
    totalBruto() {
      return this.productosFiltrados.reduce((sum, i) => sum + (Number(i.TotalBruto) || 0), 0);
    },
    totalNeto() {
      return this.productosFiltrados.reduce((sum, i) => sum + (Number(i.TotalNeto) || 0), 0);
    },
  },
  methods: {
    cerrarToast() {
      this.mostrarToast = false;
    },
    mostrarAlerta(mensaje, tipo = "success") {
      this.alerta = { mensaje, tipo };
      this.mostrarToast = true;
      setTimeout(this.cerrarToast, 3000);
    },
    formatMonto(valor) {
      return Number(valor || 0).toLocaleString("es-GT", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    async cargarInventario() {
      this.cargando = true;
      try {
        const { data } = await axios.get(`${API_URL}/inventario-global`);
        this.todos = data;
      } catch (error) {
        console.error(error);
        this.mostrarAlerta("Error al cargar el inventario global.", "danger");
      } finally {
        this.cargando = false;
      }
    },
    limpiarFiltros() {
      this.filtroNombre = "";
      this.filtroSeccion = "";
      this.ordenarPor = "";
    },
    descargarPDF() {
      const params = {};
      if (this.filtroNombre.trim()) params.nombre = this.filtroNombre.trim();
      if (this.filtroSeccion) params.seccion = this.filtroSeccion;
      if (this.ordenarPor) {
        const [campo, dir] = this.ordenarPor.split("-");
        params.ordenar_por = campo;
        params.direccion = dir;
      }

      axios
        .get(`${API_URL}/reporte-inventario-global/pdf`, {
          responseType: "blob",
          params,
        })
        .then((response) => {
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", "Inventario_Global.pdf");
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        })
        .catch((error) => {
          console.error("Error descargando PDF:", error);
          this.mostrarAlerta("Error al generar el reporte PDF.", "danger");
        });
    },
  },
  mounted() {
    this.cargarInventario();
  },
};
</script>
